import React, { useState, useEffect } from 'react';

import cookie from 'react-cookies';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import { Route, Routes, BrowserRouter as Router, Navigate } from 'react-router-dom';
import Loader from 'react-spinners/SyncLoader';

import axios from 'axios';
import styled from 'styled-components';

import { PaymentInstrumentNavigator } from './components/Bnpl/PaymentSection/PaymentInstrumentNavigator';
import ErrorBoundary from './components/ErrorBoundary';
import { ScrollToTopWithLocation } from './components/ScrollToTop';
import TokenRefresher from './components/TokenRefresher';

import UserProvider from './context/UserContext';

import { load, RudderProvider } from './hooks/rudder';
import { FormProvider } from './hooks/useForm';

import TenantConfig from './config';
import AcceptInvite from './pages/AcceptInvite';
import Accepted from './pages/Accepted';
import ActivityDetails from './pages/ActivityDetails';
import AdditionalDocs from './pages/AdditionalDocs';
import Approved from './pages/Approved';
import Auth from './pages/Auth';
import AutomatedReview from './pages/AutomatedReview';
import AwaitingSelection from './pages/Awaiting-selection';
import ChangePlaid from './pages/ChangePlaid';
import InClosing from './pages/Closing';
import CreditLineRecentActivities from './pages/CreditLineRecentActivities';
import Dashboard from './pages/Dashboard';
import Denied from './pages/Denied';
import Docusign from './pages/Docusign';
import DocusignCallback from './pages/DocusignCallback';
import DrawDown from './pages/DrawDown';
import ErrorPage from './pages/ErrorPage';
import ExternalRedirect from './pages/ExternalRedirect';
import FinishLater from './pages/FinishLater';
import Funded from './pages/Funded';
import Home from './pages/Home';
import Invite from './pages/Invite';
import Landing from './pages/Landing';
import { LoanDetail } from './pages/LoanDetail';
import LoanDetailsPage from './pages/LoanDetailsPage';
import LoanDocumentsPage from './pages/LoanDocumentsPage';
import LoanPayment from './pages/LoanPayment';
import LoanStatementsPage from './pages/LoanStatementsPage';
import LockFailed from './pages/LockFailed';
import MayQualify from './pages/MayQualify';
import NeedDocs from './pages/NeedDocs';
import PlaidOAuth from './pages/PlaidOAuth';
import PreQualify from './pages/PreQualify';
import Profile from './pages/Profile';
import Review from './pages/Review';
import ReviewInProgress from './pages/ReviewInProgress';
import Selected from './pages/Selected';
import SetTokenPage from './pages/SetToken';
import ApplicationSubmittedPage from './pages/Submited';
import TransferOut from './pages/TransferOut';
import WaitListed from './pages/WaitList';
import Welcome from './pages/Welcome';

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${({ background }) => background};
`;
const CenteredLoader = styled.div`
  position: absolute;
  top: 50%;
  right: 50%;
`;

const tenant = TenantConfig.getTenant();

function App() {
  const [isLoadingStyles, setIsLoadingStyles] = useState(true);
  useEffect(() => {
    import(`./assets/styles/tenantStyles/${tenant}.scss`)
      .then(() => {})
      .catch((e) => console.log('e', e))
      .finally(() => setIsLoadingStyles(false));
  }, [setIsLoadingStyles]);

  const [analytics, setAnalytics] = useState();
  const helmetContext = {};

  useEffect(() => {
    const rudderAnalytics = require('rudder-sdk-js');
    load(rudderAnalytics);
    setAnalytics(rudderAnalytics);
    if (typeof newrelic !== 'undefined') {
      newrelic.setCustomAttribute('tenant', tenant);
    }
    axios.interceptors.request.use((config) => {
      cookie.save('lendistry_last_interaction', new Date().getTime(), { path: '/' });
      return config;
    });
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error?.response?.status === 401) {
          // on 401 we only report smarttstreet issues
          // this helps id potential license/key problem
          if (error?.config?.url.includes('smartystreets')) {
            if (typeof newrelic !== 'undefined') {
              let smartyStreetError =
                error?.response?.data?.errors?.length > 0
                  ? error?.response?.data?.errors[0].message
                  : 'Error was not parsed!';
              newrelic.noticeError(
                'Could not connect to smarty street! Check API KEY! Error: ' + smartyStreetError
              );
            }
            NotificationManager.warning(
              'An error occurred with the address service. If the error persists, please try again later.',
              'Warning',
              10000
            );
            return Promise.reject('Could not connect to smarty street! Check API KEY!');
          } else {
            cookie.remove('lendistry_tokens_access', { path: '/' });
            cookie.remove('lendistry_tokens_refresh', { path: '/' });
            NotificationManager.warning(
              'Your session has expired, please login again.',
              'Warning',
              10000
            );
            if (!window.location.pathname?.includes('/auth/sign-in')) {
              window.location.href = `/auth/sign-in`;
            }
          }
        } else {
          // else we want to push API issues to rudderstack so we can try to understand user issues vs imcomplete apps etc.
          let errorMessage =
            error?.response?.data?.errors?.length > 0 && error?.response?.data?.errors[0].message
              ? error?.response?.data?.errors[0].message
              : error?.response?.data?.errors?.length > 0 && error?.response?.data?.errors[0].code
              ? error?.response?.data?.errors[0].code
              : 'error from API could not be parsed';
          rudderAnalytics.track('API error', error?.response?.data?.errors);
          //console.error('API error: ' + errorMessage, error?.response?.data?.errors);
        }
        return Promise.reject(error);
      }
    );
  }, []);

  if (isLoadingStyles) {
    console.log('loading');
    return (
      <CenteredLoader>
        <Loader />
      </CenteredLoader>
    );
  }
  return (
    <Router>
      <ErrorBoundary>
        <HelmetProvider context={helmetContext}>
          <UserProvider>
            <RudderProvider rudder={analytics}>
              {process.env.REACT_APP_USE_MOCK !== 'true' && <TokenRefresher />}
              <FormProvider>
                <Helmet>
                  <title>{TenantConfig?.general?.title}</title>
                  <meta name="description" content={TenantConfig?.general?.description} />
                </Helmet>
                <AppContainer background={TenantConfig?.general?.backgroundColor}>
                  <ScrollToTopWithLocation>
                    <Routes>
                      <Route path="/" index element={<Welcome />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="account/:id" element={<LoanDetailsPage />} />
                      <Route path="account/:id/payment" element={<LoanPayment />} />
                      <Route path="account/:id/documents" element={<LoanDocumentsPage />} />
                      <Route path="account/:id/statements" element={<LoanStatementsPage />} />
                      <Route
                        path="account/:id/managePayment/*"
                        element={<PaymentInstrumentNavigator />}
                      />
                      <Route path="apply" element={<Home />} />
                      <Route path="application/:id" element={<Home />} />
                      <Route path="product/:loanProductId/application/:id" element={<Home />} />
                      <Route path="application/:id/lock-failed" element={<LockFailed />} />
                      <Route path="logout" element={<Welcome />} />
                      <Route
                        path="auth/success"
                        element={<ExternalRedirect link={`${process.env.REACT_APP_ADMIN_URL}`} />}
                      />
                      <Route path="auth/*" element={<Auth />} />
                      <Route path="token" element={<SetTokenPage />} />
                      <Route path="plaid-change" element={<ChangePlaid />} />
                      <Route path="oauth/plaid" element={<PlaidOAuth />} />
                      <Route path="review" element={<Review />} />
                      <Route path="need-docs" element={<NeedDocs />} />
                      <Route path="additional-docs" element={<AdditionalDocs />} />
                      <Route path="docusign" element={<Docusign />} />
                      <Route path="closing" element={<InClosing />} />
                      <Route path="docusign-callback" element={<DocusignCallback />} />
                      <Route
                        path="account/loan/:loanProductId/application/:id"
                        element={<Home />}
                      />
                      <Route path="denied" element={<Denied />} />
                      <Route path="approved" element={<Approved />} />
                      <Route path="may-qualify" element={<MayQualify />} />
                      <Route path="pre-qualify" element={<PreQualify />} />
                      <Route path="accepted" element={<Accepted />} />
                      <Route path="awaiting-selection" element={<AwaitingSelection />} />
                      <Route path="wait-listed" element={<WaitListed />} />
                      <Route path="funded" element={<Funded />} />
                      <Route path="selected" element={<Selected />} />
                      <Route path="submitted" element={<ApplicationSubmittedPage />} />
                      <Route path="automated-review" element={<AutomatedReview />} />
                      <Route path="review-in-progress" element={<ReviewInProgress />} />
                      <Route path="landing" element={<Landing />} />
                      <Route path="finish-later" element={<FinishLater />} />
                      <Route path="loan-detail" element={<LoanDetail />} />
                      <Route path="invite/:code" element={<Invite />} />
                      <Route path="invite/redirect/:inviteCode" element={<AcceptInvite />} />
                      <Route path="invite/:code/application/:appId" element={<Invite />} />
                      <Route path="error/invite" element={<ErrorPage />} />
                      <Route path="offer/:id" element={<Navigate to={`/landing`} />} />
                      <Route path="account/:id/draw-down" element={<DrawDown />} />
                      <Route path="account/:id/transfer-out" element={<TransferOut />} />
                      <Route
                        path="account/:id/recent-activities"
                        element={<CreditLineRecentActivities />}
                      />
                      <Route
                        path="account/:id/activities/:activityId"
                        element={<ActivityDetails />}
                      />
                    </Routes>
                  </ScrollToTopWithLocation>
                </AppContainer>
                <NotificationContainer />
              </FormProvider>
            </RudderProvider>
          </UserProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
